import {
  nonZeroPriceSchema,
  objectIdSchema,
  paginationWithSearchSchema,
  Route,
  RouteSchema,
  routeRegionSchema,
  temperatureTypeSchema,
  tripLoadTypeSchema,
  tripTypeSchema,
  vehicleTypesSchema,
} from '@hero/schema';
import { API } from '..';
import { z } from 'zod';

//* common
export const routeRegionAPISchema = routeRegionSchema
  .pick({
    displayName: true,
    type: true,
    _id: true,
  })
  .partial({ type: true, displayName: true });
export type RouteRegionAPI = z.infer<typeof routeRegionAPISchema>;

//* get one
export const routeGetOneAPISchema = RouteSchema.model.pick({
  _id: true,
});
export type RouteGetOneAPI = API<z.infer<typeof routeGetOneAPISchema>, Route>;

//* get all
/**
 * List filter. The legacy `isPlatformRoute` / `orgId` shape is gone in v2.
 * Filters:
 *   - `sellerId` / `buyerId` — scope to the seller or buyer side. Combine
 *     both for a directed Route lookup.
 *   - `isOwnRoute` — `true` for open Routes, `false` for directed Routes.
 *   - `agreementId` — Routes bound to a specific Agreement.
 *   - `vehicle` — single vehicle match against the Route's `vehicles[]` array.
 */
export const routeGetAllAPISchema = z
  .object({
    sellerId: objectIdSchema.optional(),
    buyerId: objectIdSchema.optional(),
    agreementId: objectIdSchema.optional(),
    /**
     * Boolean from a URL query string.
     *
     * `z.coerce.boolean()` calls `Boolean(v)` which treats the string `"false"`
     * as truthy (any non-empty string is `true`), so a `?isOwnRoute=false`
     * filter silently flips to `true` on the wire. We preprocess explicitly
     * so `"true"`/`"false"` map to the matching boolean and real booleans pass
     * through unchanged.
     */
    isOwnRoute: z
      .preprocess(
        v => (v === 'true' ? true : v === 'false' ? false : v),
        z.boolean(),
      )
      .optional(),
    vehicle: vehicleTypesSchema.optional(),
    tripType: tripTypeSchema.optional(),
  })
  .extend(paginationWithSearchSchema.shape)
  .partial();
export type RouteGetAllAPI = API<z.infer<typeof routeGetAllAPISchema>, Route[]>;

//* create base shape (without refinement so partial() works for update)
const routeCreateBaseShape = {
  sellerId: objectIdSchema.optional(),
  buyerId: objectIdSchema.optional(),
  agreementId: objectIdSchema.optional(),
  loadType: tripLoadTypeSchema,
  tripType: tripTypeSchema,
  vehicles: z.array(vehicleTypesSchema).min(1),
  temperatureType: z.array(temperatureTypeSchema).min(1).optional(),
  destinations: z.array(routeRegionAPISchema).min(2),
  price: nonZeroPriceSchema,
  currency: RouteSchema.model.shape.currency.optional(),
  status: RouteSchema.model.shape.status.optional(),
  expectedOrders: RouteSchema.model.shape.expectedOrders.optional(),
};
const routeCreateAPIBaseSchema = z.object(routeCreateBaseShape);

/**
 * Create a Route. Server enforces:
 *   - at least one of `sellerId` / `buyerId`;
 *   - if `agreementId` is set, both must be set AND match `Agreement.parties[]`;
 *   - no vehicle overlap with an existing active Route on the same lane;
 *   - the caller must be the seller or buyer (or a super admin).
 */
export const routeCreateAPISchema = routeCreateAPIBaseSchema.refine(
  v => !!v.sellerId || !!v.buyerId,
  { message: 'route_at_least_one_of_seller_or_buyer_required' },
);
export type RouteCreateAPI = API<z.infer<typeof routeCreateAPISchema>, Route>;

//* create multi
export const routeCreateMultiAPISchema = z.object({
  data: z.array(routeCreateAPISchema).min(1),
});
export type RouteCreateMultiAPI = API<z.infer<typeof routeCreateMultiAPISchema>, Route[]>;

//* update
export const routeUpdateAPISchema = routeCreateAPIBaseSchema.partial().extend({
  _id: objectIdSchema,
});
export type RouteUpdateAPI = API<z.infer<typeof routeUpdateAPISchema>, Route>;

//* update multi
export const routeUpdateMultiAPISchema = z.object({
  data: z.array(routeUpdateAPISchema).min(1),
});
export type RouteUpdateMultiAPI = API<z.infer<typeof routeUpdateMultiAPISchema>, Route[]>;

//* delete route
export const routeDeleteAPISchema = RouteSchema.model.pick({
  _id: true,
});
export type RouteDeleteAPI = API<z.infer<typeof routeDeleteAPISchema>, void>;
